import {Injectable} from '@angular/core';
import {cloneDeep, isFunction} from 'lodash';
import {TenantsRestService} from '../apis/tenants-rest.service';
import {RegionsRestService} from '../apis/regions-rest.service';
import {DevicesRestService} from '../apis/devices-rest.service';
import {SitesRestService} from '../apis/sites-rest.service';
import {LinksRestService} from '../apis/links-rest.service';
import {AuthorizationService} from '../../../common/services/authorization.service';
import {WsEntitiesService} from '../../../common/services/communication/ws-entities.service';
import {NetworkBaseTreeBuilderService, TreeEntityTypeStandard} from './network-base-tree-builder.service';

export const DEFAULT_ENTITY_TYPES_LIST: TreeEntityTypeStandard[] = ['tenant', 'region', 'device', 'link', 'site'];

@Injectable({providedIn: 'root'})
export class NetworkTreeDataBuilderService extends NetworkBaseTreeBuilderService<TreeEntityTypeStandard> {

    constructor(private tenantsRestService: TenantsRestService,
                private regionsRestService: RegionsRestService,
                private devicesRestService: DevicesRestService,
                private sitesRestService: SitesRestService,
                private linksRestService: LinksRestService,
                private authorizationService: AuthorizationService,
                private wsEntitiesService: WsEntitiesService) {

        super(DEFAULT_ENTITY_TYPES_LIST, {
            tenant: {
                getEntities: (tenantId, entityFilter) => this.tenantsRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter
                })
            },
            region: {
                parents: ['tenant'],
                getParentId: (entity) => this.getTenantId(entity),
                getEntities: (tenantId, entityFilter) => this.regionsRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter
                }),
            },
            device: {
                parents: ['region'],
                getParentId: (entity) => this.getRegionId(entity),
                artificialParentType: 'region',
                artificialName: 'Devices',
                getEntities: (tenantId, entityFilter) => this.devicesRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter
                })
            },
            link: {
                parents: ['region'],
                getParentId: (entity) => this.getRegionId(entity),
                artificialParentType: 'region',
                artificialName: 'Links',
                getEntities: (tenantId, entityFilter) => this.linksRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter
                }),
                enrichEntities: (entities: any) => this.enrichLinks(entities),
            },
            site: {
                parents: ['region'],
                getParentId: (entity) => this.getRegionId(entity),
                artificialParentType: 'region',
                artificialName: 'Sites',
                getEntities: (tenantId, entityFilter) => this.sitesRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter
                })
            },
            ad: {
                parents: ['tenant'],
                getParentId: (entity) => this.getTenantId(entity),
                getEntities: (...args) => this.getUserEntities(...args),
            },
        });
    }

    getTenantId(entity: any) {
        return entity.tenantId;
    }

    getRegionId(entity: any) {
        return entity.regionId;
    }

    enrichLinks(entities: any) {
        if (this.authorizationService.validForMonitorLinkOrOneLiveTenantUserOrGroupUsers()) {
            return;
        }

        const links = Object.values(entities.link);
        const regionIndex = entities.region || {};
        const deviceIndex = entities.device || {};
        const siteIndex = entities.site || {};

        links.forEach((link: any) => {
            const deviceSrc = deviceIndex[link.linkSrcId] || cloneDeep(this.devicesRestService.getEntityById(link.linkSrcId));
            if (!deviceSrc) {
                return;
            }

            const srcParent = regionIndex[deviceSrc.regionId];
            link.linkSrcTenantId = srcParent.tenantId;

            const deviceDst = deviceIndex[link.linkDstId] || siteIndex[link.linkDstId] ||
                cloneDeep(this.devicesRestService.getEntityById(link.linkDstId) || this.sitesRestService.getEntityById(link.linkDstId));;


            if (deviceDst && regionIndex[deviceDst.regionId]) {
                link.linkDstTenantId = regionIndex[deviceDst.regionId].tenantId;
            }
        });
    }

    getUserEntities(tenantId: number | string, entityFilter) {
        let entities = this.wsEntitiesService.getEntitiesArray('ads');

        for (const entity of entities) {
            entity.text = entity.name;
        }

        if (tenantId) {
            entities = entities.filter((entity: any) => entity.tenantId === tenantId);
        }
        if (isFunction(entityFilter)) {
            entities = entities.filter(entityFilter);
        }

        return entities;
    }
}
